import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ConfirmDialogOptions, ConfirmDialogService, TemplateDirective } from '@fleet-beat/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { ArrayEditorItem } from '../../models/array-editor-item.model';
import { ArrayEditorModel } from '../../models/array-editor.model';
import { EditorConfig } from '../../models/editor-config.model';
import { EditorHelperService } from '../../services/editor-helper.service';

@Component({
  selector: 'fb-array-editor',
  templateUrl: './array-editor.component.html',
  styleUrls: ['./array-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArrayEditorComponent extends OnDestroyMixin implements OnInit {
  @Input() set form(value: FormGroup) {
    this.formSubject.next(value);
  }
  get form(): FormGroup {
    return this.formSubject.value;
  }

  get array(): FormArray {
    return this.formSubject.value.get('_items') as FormArray;
  }

  get itemsCurrentValue(): ArrayEditorModel<object> {
    return {
      items: this.itemsCollection
        ? this.itemsCollection.map((item: ArrayEditorItem) => item.value || item.initialValue).filter(Boolean)
        : [],
      added: this.itemsCollection
        ? this.itemsCollection
            .filter((item: ArrayEditorItem) => item.newItem)
            .map((item: ArrayEditorItem) => item.value || item.initialValue)
            .filter(Boolean)
        : [],
      updated: this.itemsCollection
        ? this.itemsCollection
            .filter((item: ArrayEditorItem) => !item.newItem && item.changed)
            .map((item: ArrayEditorItem) => item.value || item.initialValue)
            .filter(Boolean)
        : [],
      removed: this.removedItemsCollection
        ? this.removedItemsCollection.map((item: ArrayEditorItem) => item.value || item.initialValue).filter(Boolean)
        : [],
    };
  }
  @Input() addButtonCaption: string = 'Add';
  @Input() addButtonAlign: 'left' | 'center' | 'right' = 'right';
  @Input() atLeastOneItem: boolean = true;
  @Input() canRemove: boolean = true;
  @Input() canAdd: boolean = true;
  @Input() canAction: boolean = false;
  @Input() confirmRemove: boolean = false;
  @Input() itemsTemplateName: string = 'itemsTemplate';
  @Input() addButtonTemplateName: string;
  @Input() actionButtonTemplateName: string;
  @Input() removeButtonTemplateName: string;
  @Input() removeButtonClasses: string;
  @Input() templates: QueryList<TemplateDirective<object>>;
  @Input() itemConfig: EditorConfig;
  @Output() actionEvent: EventEmitter<string> = new EventEmitter(undefined);

  itemsCollection: ArrayEditorItem[] = [];
  removedItemsCollection: ArrayEditorItem[] = [];

  @ContentChildren(TemplateDirective) childrenTemplates: QueryList<TemplateDirective<object>>;

  private muteEvents: boolean;
  private readonly formSubject: BehaviorSubject<FormGroup> = new BehaviorSubject(undefined);

  private readonly deleteConfirmConfig: ConfirmDialogOptions<boolean> = {
    title: 'Delete Item',
    text: 'Are you sure that you want to delete this item?',
    additionText: 'This action cannot be undone.',
    buttons: [
      {
        caption: 'Cancel',
        action: false,
        color: 'empty',
      },
      {
        caption: 'Delete',
        color: 'success',
        action: true,
      },
    ],
  };

  constructor(
    private editorHelperService: EditorHelperService,
    private confirmDialogService: ConfirmDialogService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.formSubject
      .pipe(untilComponentDestroyed(this), filter(Boolean))
      .subscribe((form: FormGroup) => this.listenForm(form));

    this.addInitialItem();
  }

  onAddItem(): void {
    this.addItem();
  }

  onRemoveItem(index: number): void {
    this.removeItem(index);
  }

  onAction(): void {
    if (this.form.valid) {
      this.actionEvent.emit();
    }
  }

  private listenForm(form: FormGroup): void {
    this.editorHelperService.initializeArrayControls(form);
    const itemsControl: FormControl = form.get('items') as FormControl;

    itemsControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        filter(Boolean),
        filter(() => !this.muteEvents),
        distinctUntilChanged(),
      )
      .subscribe((items: object[]) => {
        this.initializeArrayAndMuteEvents(form, items);
        this.cdr.markForCheck();
      });

    form
      .get('_items')
      .valueChanges.pipe(
        untilComponentDestroyed(this),
        filter(Boolean),
        filter(() => !this.muteEvents),
      )
      .subscribe(() => {
        this.muteEvents = true;

        this.form.patchValue(this.itemsCurrentValue);

        this.muteEvents = false;
      });

    this.initializeArrayAndMuteEvents(form, itemsControl.value);
  }

  private initializeArrayAndMuteEvents(form: FormGroup, items: object[]): void {
    this.muteEvents = true;
    this.initializeArray(form, items);
    this.muteEvents = false;
  }

  private initializeArray(form: FormGroup, items: object[]): void {
    const array: FormArray = form.get('_items') as FormArray;

    this.itemsCollection = [];
    this.removedItemsCollection = [];
    array.clear();

    if (!items?.length) {
      this.addInitialItem();
      return;
    }

    items.forEach((item: object) => {
      const arrayItem: ArrayEditorItem = {
        initialValue: item,
        newItem: false,
        changed: false,
        removeEvent: new EventEmitter(),
      };

      const control: AbstractControl = this.itemConfig
        ? this.editorHelperService.createForm(this.itemConfig, item)
        : new FormControl();

      this.attachItem(arrayItem, control);
    });
  }

  private addInitialItem(): void {
    if (!this.itemsCollection.length && this.atLeastOneItem) {
      this.addItem();
    }
  }

  private addItem(): void {
    const item: ArrayEditorItem = {
      initialValue: null,
      newItem: true,
      changed: false,
      removeEvent: new EventEmitter(),
    };
    const control: AbstractControl = this.itemConfig
      ? this.editorHelperService.createForm(this.itemConfig, item)
      : new FormControl();

    this.attachItem(item, control);
    this.form.markAsDirty();
  }

  private removeItem(index: number): void {
    if (this.confirmRemove) {
      this.confirmDialogService
        .confirm<boolean>(this.deleteConfirmConfig)
        .pipe(untilComponentDestroyed(this), filter(Boolean))
        .subscribe(() => {
          this.removeItemEx(index);
          this.cdr.markForCheck();
        });
    } else {
      this.removeItemEx(index);
    }
  }

  private removeItemEx(index: number): void {
    const item: ArrayEditorItem = this.itemsCollection[index];
    if (!item.newItem) {
      this.removedItemsCollection.push(item);
    }

    this.itemsCollection.splice(index, 1);
    this.array.removeAt(index);
    this.form.markAsDirty();
  }

  private attachItem(item: ArrayEditorItem, control: AbstractControl): void {
    item.removeEvent.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.removeItem(item.index);
    });

    control.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        filter(() => !control.pristine),
        tap((value: object) => {
          item.value = {
            ...item.initialValue,
            ...value,
          };
          item.changed = true;
        }),
      )
      .subscribe();

    this.itemsCollection.push(item);
    this.array.push(control);
  }
}
